import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { TableSkeleton } from "@/components/common";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";

const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const ForgotPassword = lazy(() => import("@/pages/PasswordReset").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/PasswordReset").then((m) => ({ default: m.ResetPassword })));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const POS = lazy(() => import("@/pages/POS"));
const Sales = lazy(() => import("@/pages/Sales"));
const Shifts = lazy(() => import("@/pages/Shifts"));
const Products = lazy(() => import("@/pages/Products"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Expiry = lazy(() => import("@/pages/Expiry"));
const ExpiredPlan = lazy(() => import("@/pages/ExpiredPlan"));
const Purchases = lazy(() => import("@/pages/Purchases"));
const ReceiptEditor = lazy(() => import("@/pages/ReceiptEditor"));
const Capture = lazy(() => import("@/pages/Capture"));
const InvoiceScan = lazy(() => import("@/pages/InvoiceScan"));
const PriceCheck = lazy(() => import("@/pages/PriceCheck"));
const StockReport = lazy(() => import("@/pages/StockReport"));
const CountSheets = lazy(() => import("@/pages/CountSheets"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Payables = lazy(() => import("@/pages/Payables"));
const ProductCleanup = lazy(() => import("@/pages/ProductCleanup"));
const Reports = lazy(() => import("@/pages/Reports"));
const Attendance = lazy(() => import("@/pages/Attendance"));
const Payroll = lazy(() => import("@/pages/Payroll"));
const Assistant = lazy(() => import("@/pages/Assistant"));
const ImportWizard = lazy(() => import("@/pages/ImportWizard"));
const RxReview = lazy(() => import("@/pages/RxReview"));
const Staff = lazy(() => import("@/pages/Staff"));
const Security = lazy(() => import("@/pages/Security"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const StockDesk = lazy(() => import("@/pages/StockDesk"));
const OutOfStock = lazy(() => import("@/pages/OutOfStock"));
const CostReview = lazy(() => import("@/pages/CostReview"));
const MarginWatch = lazy(() => import("@/pages/MarginWatch"));
const UnitReview = lazy(() => import("@/pages/UnitReview"));
const Auditor = lazy(() => import("@/pages/Auditor"));
const StockCounts = lazy(() => import("@/pages/StockCounts"));
const StockAssistant = lazy(() => import("@/pages/StockAssistant"));
const Tills = lazy(() => import("@/pages/Tills"));
const ApprovalLimits = lazy(() => import("@/pages/ApprovalLimits"));
const Audit = lazy(() => import("@/pages/Audit"));
const SettingsPage = lazy(() => import("@/pages/Settings"));
const Platform = lazy(() => import("@/pages/Platform"));
const Leads = lazy(() => import("@/pages/Leads"));
const Care = lazy(() => import("@/pages/Care"));
const CareIntake = lazy(() => import("@/pages/CareIntake"));
const CareCase = lazy(() => import("@/pages/CareCase"));
const CareLink = lazy(() => import("@/pages/CareLink"));
const ProductGaps = lazy(() => import("@/pages/ProductGaps"));

function Protected({ perm, children }) {
  const { session, can } = useAuth();
  if (session === null) return <div className="p-10"><TableSkeleton /></div>;
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  if (perm && !can(perm)) return <div className="pf-card p-10 text-center" data-testid="forbidden">You don't have access to this page.</div>;
  return children;
}

function Home() {
  const { session } = useAuth();
  if (session === null) return null;
  return session ? <Navigate to={homeFor(session)} replace /> : <Landing />;
}

const P = (perm, el) => <Protected perm={perm}>{el}</Protected>;

export default function App() {
  useEffect(() => { if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") navigator.serviceWorker.register("/sw.js").catch(() => {}); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<div className="p-10"><TableSkeleton /></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/welcome" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/care-link" element={<CareLink />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/app" element={P("dashboard", <Dashboard />)} />
              <Route path="/app/pos" element={P("pos.sell", <POS />)} />
              <Route path="/app/sales" element={P("sales.view", <Sales />)} />
              <Route path="/app/shifts" element={P("shift.own", <Shifts />)} />
              <Route path="/app/products" element={P("catalog.view", <Products />)} />
              <Route path="/app/products/fill-details" element={P("catalog.edit", <ProductGaps />)} />
              <Route path="/app/inventory" element={P("stock.view", <Inventory />)} />
              <Route path="/app/expiry" element={P("stock.view", <Expiry />)} />
              <Route path="/app/expiry/plan" element={P("stock.view", <ExpiredPlan />)} />
              <Route path="/app/purchases" element={P("receiving", <Purchases />)} />
              <Route path="/app/receipts/:id" element={P("receiving", <ReceiptEditor />)} />
              <Route path="/app/receive/capture" element={P("receiving", <Capture />)} />
              <Route path="/app/receive/invoice" element={P("receiving", <InvoiceScan />)} />
              <Route path="/app/price-check" element={P("catalog.cost", <PriceCheck />)} />
              <Route path="/app/inventory/stock-report" element={P("stock.adjust", <StockReport />)} />
              <Route path="/app/inventory/count-sheets" element={P("stock.adjust", <CountSheets />)} />
              <Route path="/app/suppliers" element={P("suppliers", <Suppliers />)} />
              <Route path="/app/payables" element={P("suppliers", <Payables />)} />
              <Route path="/app/product-cleanup" element={P("catalog.view", <ProductCleanup />)} />
              <Route path="/app/reports" element={P("reports", <Reports />)} />
              <Route path="/app/attendance" element={P("attendance.own", <Attendance />)} />
              <Route path="/app/payroll" element={P("attendance.manage", <Payroll />)} />
              <Route path="/app/assistant" element={P("catalog.view", <Assistant />)} />
              <Route path="/app/products/import" element={P("catalog.edit", <ImportWizard />)} />
              <Route path="/app/products/rx-review" element={P("catalog.edit", <RxReview />)} />
              <Route path="/app/staff" element={P("staff.manage", <Staff />)} />
              <Route path="/app/security" element={<Security />} />
              <Route path="/app/expenses" element={P("expenses", <Expenses />)} />
              <Route path="/app/stock-desk" element={P("stock.view", <StockDesk />)} />
              <Route path="/app/out-of-stock" element={P("stock.view", <OutOfStock />)} />
              <Route path="/app/cost-review" element={P("catalog.cost", <CostReview />)} />
              <Route path="/app/margin-watch" element={P("catalog.cost", <MarginWatch />)} />
              <Route path="/app/unit-review" element={P("catalog.view", <UnitReview />)} />
              <Route path="/app/auditor" element={P("auditor", <Auditor />)} />
              <Route path="/app/stock-counts" element={P("stock.adjust", <StockCounts />)} />
              <Route path="/app/stock-assistant" element={P("stock.view", <StockAssistant />)} />
              <Route path="/app/tills" element={P("tills.manage", <Tills />)} />
              <Route path="/app/approval-limits" element={P("settings", <ApprovalLimits />)} />
              <Route path="/app/audit" element={P("audit", <Audit />)} />
              <Route path="/app/settings" element={P("settings", <SettingsPage />)} />
              <Route path="/app/leads" element={P("leads.view", <Leads />)} />
              <Route path="/app/care" element={P("care.intake", <Care />)} />
              <Route path="/app/care/new" element={P("care.intake", <CareIntake />)} />
              <Route path="/app/care/:id" element={P("care.intake", <CareCase />)} />
              <Route path="/app/care/:id/review" element={P("care.intake", <CareCase review />)} />
              <Route path="/platform" element={P("platform", <Platform />)} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
